import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RegistryCategory, RegistrySeries } from "../../registry";
import { useCreateSeries, useUpdateSeries } from "./mutations";
import { useGenres } from "./useGenres";
import { useMyTags } from "./useTags";

const AGE_OPTIONS = [
  { value: "p3", label: "3+" },
  { value: "p7", label: "7+" },
  { value: "p12", label: "12+" },
  { value: "p16", label: "16+" },
  { value: "p18", label: "18+" },
] as const;

function hasErrors(field: { state: { meta: { errors: unknown[] } } }) {
  return field.state.meta.errors.length > 0;
}

interface SeriesFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: RegistryCategory[];
  showCategories: boolean;
  series?: RegistrySeries;
}

export function SeriesFormDialog({
  open,
  onOpenChange,
  categories,
  showCategories,
  series,
}: SeriesFormDialogProps) {
  const isEditing = !!series;
  const createMutation = useCreateSeries();
  const updateMutation = useUpdateSeries();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  const { data: genres } = useGenres();
  const { data: suggestedTags } = useMyTags();

  // Tag autocomplete UI state (not part of form values)
  const [tagInput, setTagInput] = useState("");
  const coverInputRef = useRef<HTMLInputElement>(null);
  const artworkInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const titleArtworkInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: {
      title: "",
      description: "",
      categoryId: "",
      genreId: "",
      ageRestriction: "",
      tags: [] as string[],
      credits: "",
      coverFile: null as File | null,
      sensitiveContent: false,
      artworkFile: null as File | null,
      audioFile: null as File | null,
      titleArtworkFile: null as File | null,
      secondaryGenreIds: [] as string[],
    },
    onSubmit: ({ value }) => {
      const formData = new FormData();
      formData.append("title", value.title);
      if (value.description) formData.append("description", value.description);
      if (value.categoryId) formData.append("category", value.categoryId);
      if (value.genreId) formData.append("genre", value.genreId);
      if (value.ageRestriction)
        formData.append("age_restriction", value.ageRestriction);
      if (value.credits) formData.append("credits", value.credits);
      if (value.coverFile) formData.append("cover", value.coverFile);
      formData.append(
        "sensitive_content",
        value.sensitiveContent ? "true" : "false",
      );
      if (value.artworkFile) formData.append("artwork", value.artworkFile);
      if (value.audioFile) formData.append("audio", value.audioFile);
      if (value.titleArtworkFile)
        formData.append("title_artwork", value.titleArtworkFile);
      for (const id of value.secondaryGenreIds) {
        formData.append("secondary_genres", id);
      }
      for (const tag of value.tags) {
        formData.append("tags", tag);
      }

      if (isEditing && series) {
        updateMutation.mutate(
          { pk: series.pk, data: formData },
          { onSuccess: () => onOpenChange(false) },
        );
      } else {
        createMutation.mutate(formData, {
          onSuccess: () => onOpenChange(false),
        });
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset form state only when dialog opens
  useEffect(() => {
    if (open) {
      form.reset();
      form.setFieldValue("title", series?.title ?? "");
      form.setFieldValue("description", series?.description ?? "");
      form.setFieldValue("categoryId", series?.category?.toString() ?? "");
      form.setFieldValue("genreId", series?.genre?.id?.toString() ?? "");
      form.setFieldValue("ageRestriction", series?.ageRestriction ?? "");
      form.setFieldValue("tags", series?.tags ?? []);
      form.setFieldValue("credits", series?.credits ?? "");
      form.setFieldValue("coverFile", null);
      form.setFieldValue("sensitiveContent", series?.sensitiveContent ?? false);
      form.setFieldValue("artworkFile", null);
      form.setFieldValue("audioFile", null);
      form.setFieldValue("titleArtworkFile", null);
      form.setFieldValue(
        "secondaryGenreIds",
        series?.secondaryGenres?.map((g) => g.id.toString()) ?? [],
      );
      setTagInput("");
      if (coverInputRef.current) coverInputRef.current.value = "";
      if (artworkInputRef.current) artworkInputRef.current.value = "";
      if (audioInputRef.current) audioInputRef.current.value = "";
      if (titleArtworkInputRef.current) titleArtworkInputRef.current.value = "";
      createMutation.reset();
      updateMutation.reset();
    }
  }, [open, series]);

  const tags = useStore(form.store, (s) => s.values.tags);
  const secondaryGenreIds = useStore(
    form.store,
    (s) => s.values.secondaryGenreIds,
  );
  const primaryGenreId = useStore(form.store, (s) => s.values.genreId);

  const filteredSuggestions = useMemo(() => {
    if (!suggestedTags || !tagInput.trim()) return [];
    const lower = tagInput.toLowerCase();
    return suggestedTags.filter(
      (t) => t.toLowerCase().includes(lower) && !tags.includes(t),
    );
  }, [suggestedTags, tagInput, tags]);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      form.setFieldValue("tags", [...tags, trimmed]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    form.setFieldValue(
      "tags",
      tags.filter((t) => t !== tag),
    );
  };

  // Combobox owns arrow navigation, Enter-on-highlight and Escape; only the
  // tag-specific behaviour stays here.
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (tagInput.trim()) addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit AudioGame" : "New AudioGame"}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <form.Field
            name="title"
            validators={{
              onSubmit: ({ value }) =>
                !value.trim() ? "Title is required." : undefined,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium" htmlFor="series-title">
                  Title <span className="text-destructive">*</span>
                </label>
                <Input
                  id="series-title"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Series title"
                  className={hasErrors(field) ? "border-destructive" : ""}
                />
                {hasErrors(field) && (
                  <p className="text-xs text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label
                  className="text-sm font-medium"
                  htmlFor="series-description"
                >
                  Summary
                </label>
                <textarea
                  id="series-description"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Series description (optional)"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-ring"
                  rows={3}
                />
              </div>
            )}
          </form.Field>

          {showCategories && (
            <form.Field
              name="categoryId"
              validators={{
                onSubmit: ({ value }) =>
                  showCategories && !value
                    ? "Category is required."
                    : undefined,
              }}
            >
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">
                    Category <span className="text-destructive">*</span>
                  </span>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v)}
                  >
                    <SelectTrigger
                      aria-label="Category"
                      className={`w-full ${hasErrors(field) ? "border-destructive" : ""}`}
                    >
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasErrors(field) && (
                    <p className="text-xs text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          <form.Field name="genreId">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Genre</span>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v)}
                >
                  <SelectTrigger className="w-full" aria-label="Genre">
                    <SelectValue placeholder="Select genre" />
                  </SelectTrigger>
                  <SelectContent>
                    {genres
                      ?.filter(
                        (g) => !secondaryGenreIds.includes(g.id.toString()),
                      )
                      .map((genre) => (
                        <SelectItem key={genre.id} value={genre.id.toString()}>
                          {genre.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Secondary Genres</span>
            <div className="grid grid-cols-3 gap-2">
              {([0, 1, 2] as const).map((slotIndex) => {
                const otherSelected = secondaryGenreIds.filter(
                  (_, i) => i !== slotIndex,
                );
                const excluded = new Set([primaryGenreId, ...otherSelected]);
                return (
                  <Select
                    key={slotIndex}
                    value={secondaryGenreIds[slotIndex] ?? ""}
                    onValueChange={(v) => {
                      const updated = [...secondaryGenreIds];
                      if (v === "__none__") {
                        updated.splice(slotIndex, 1);
                      } else {
                        updated[slotIndex] = v;
                      }
                      form.setFieldValue(
                        "secondaryGenreIds",
                        updated.filter(Boolean),
                      );
                    }}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label={`Secondary genre ${slotIndex + 2}`}
                    >
                      <SelectValue placeholder={`Genre ${slotIndex + 2}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {secondaryGenreIds[slotIndex] && (
                        <SelectItem value="__none__">— None —</SelectItem>
                      )}
                      {genres
                        ?.filter((g) => !excluded.has(g.id.toString()))
                        .map((genre) => (
                          <SelectItem
                            key={genre.id}
                            value={genre.id.toString()}
                          >
                            {genre.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <form.Field name="ageRestriction">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Age Restriction</span>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v)}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Age restriction"
                    >
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="sensitiveContent">
              {(field) => (
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox
                    id="series-sensitive-content"
                    checked={field.state.value}
                    onCheckedChange={(checked) =>
                      field.handleChange(checked === true)
                    }
                  />
                  <label
                    className="text-sm font-medium cursor-pointer"
                    htmlFor="series-sensitive-content"
                  >
                    Sensitive content
                  </label>
                </div>
              )}
            </form.Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Tags</span>
            <div className="flex flex-wrap gap-1.5 mb-1">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="rounded-full hover:bg-muted-foreground/20 p-0.5"
                    aria-label={`Remove tag ${tag}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </Badge>
              ))}
            </div>
            <Combobox
              label="Add a tag"
              value={tagInput}
              onValueChange={setTagInput}
              onSelect={addTag}
              options={filteredSuggestions}
              placeholder="Type a tag and press Enter"
              onKeyDown={handleTagKeyDown}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="series-cover">
              Cover
              <span className="ml-1 text-xs italic">
                &bull; Main image, which includes artwork and title on the same
                picture.
              </span>
            </label>
            {isEditing && series?.cover && (
              <form.Field name="coverFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current cover set. Upload a new file to replace it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="series-cover"
              ref={coverInputRef}
              type="file"
              accept="image/png"
              onChange={(e) =>
                form.setFieldValue("coverFile", e.target.files?.[0] ?? null)
              }
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="series-artwork">
              Artwork
              <span className="ml-1 text-xs italic">
                &bull; Artwork only, without any overlay text or title.
              </span>
            </label>
            {isEditing && series?.artwork && (
              <form.Field name="artworkFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current artwork set. Upload a new file to replace it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="series-artwork"
              ref={artworkInputRef}
              type="file"
              accept="image/png"
              onChange={(e) =>
                form.setFieldValue("artworkFile", e.target.files?.[0] ?? null)
              }
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              className="text-sm font-medium"
              htmlFor="series-title-artwork"
            >
              Title Artwork
              <span className="ml-1 text-xs italic">
                &bull; Transparent PNG for the title only.
              </span>
            </label>
            {isEditing && series?.titleArtwork && (
              <form.Field name="titleArtworkFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current title artwork set. Upload a new file to replace
                      it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="series-title-artwork"
              ref={titleArtworkInputRef}
              type="file"
              accept="image/png"
              onChange={(e) =>
                form.setFieldValue(
                  "titleArtworkFile",
                  e.target.files?.[0] ?? null,
                )
              }
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="series-audio">
              Audio
              <span className="ml-1 text-xs italic">
                &bull; MP3 theme the app plays on the season page. Goes live the
                next time any episode in this season is published.
              </span>
            </label>
            {isEditing && series?.audio && (
              <form.Field name="audioFile">
                {(field) =>
                  !field.state.value && (
                    <p className="text-xs text-muted-foreground">
                      Current audio set. Upload a new file to replace it.
                    </p>
                  )
                }
              </form.Field>
            )}
            <Input
              id="series-audio"
              ref={audioInputRef}
              type="file"
              accept="audio/mpeg,.mp3"
              onChange={(e) =>
                form.setFieldValue("audioFile", e.target.files?.[0] ?? null)
              }
            />
          </div>

          <form.Field name="credits">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium" htmlFor="series-credits">
                  Credits
                </label>
                <textarea
                  id="series-credits"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Credits (optional)"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-ring"
                  rows={2}
                />
              </div>
            )}
          </form.Field>

          {mutationError && (
            <p className="text-sm text-destructive">
              {mutationError.message || "An error occurred. Please try again."}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Series"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
